import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { Modal } from '@/components/common/modal';
import { Button } from '@/components/form/button';
import { Input } from '@/components/form/input';

import { updateFermentation } from '../../services/fermentations';
import { IFermentation } from '../../types/fermentation';

interface IProps {
  /** based on the state, modal will be rendered */
  isOpen: boolean;

  /** toggle modal open and close state */
  toggle: () => void;

  /** analysis data */
  data?: IFermentation;

  onSuccess: () => void;
}

export type FermentationTitleEditFields = {
  name: string;
};

const validator = yup.object({
  name: yup.string().required('commonValidation.fieldRequired'),
});

export const EditFermentationTitleModel: React.FC<IProps> = ({
  data,
  isOpen,
  toggle,
  onSuccess,
}) => {
  const { t } = useTranslation();

  // update fermentation mutation
  const updateFermentationMutation = useMutation({
    mutationFn: ({
      id,
      fermentation,
    }: {
      id: string;
      fermentation: Partial<IFermentation>;
    }) => updateFermentation(id, { ...fermentation }),
    onSuccess: () => {
      toggle();
      toast.success(t('Changes has been made, will be updated in shortly!'), {
        duration: 4000,
      });
      onSuccess();
    },
  });

  // based on the analysis method we switch the validation schema
  // only few input fields are getting removed from the UI based on the analysis method
  // if the validation schema become complex, consider creating two seperate validations for each type
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FermentationTitleEditFields>({
    resolver: yupResolver(validator),
  });

  const handleOnSubmit = handleSubmit((formData) => {
    const fermentation: Partial<IFermentation> = { ...formData };
    updateFermentationMutation.mutate({ id: data!.id, fermentation });
  });

  return (
    <Modal isOpen={isOpen} toggle={toggle} title="editMeasurements.modalTitle">
      <form>
        <div className="flex flex-col gap-2 mt-8">
          <Input
            name="name"
            errors={errors}
            className="ml-28"
            register={register}
            defaultValue={data!.name}
            label={t('editMeasurements.title')}
            labelStyleClass="w-full text-gray-700"
          />

          <div className="flex justify-end mt-6">
            <Button
              type="submit"
              onClick={handleOnSubmit}
              isLoading={updateFermentationMutation.isLoading}
            >
              {t('common.save')}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
